// src/store.js
import { createStore } from 'vuex';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, refreshUserToken, db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
  
const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds

const store = createStore({
  state() {
    return {
      user: null,
      idToken: null,
      tokenExpiration: null,
      loginError: '',
      isAuthenticated: false,
      userDetails: null,
      authorizationError: null,
    };
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
      state.isAuthenticated = !!user;
    },
    setIdToken(state, token) {
      state.idToken = token;
    },
    setCoreAuthData(state, authData) {
      state.coreAuthToken = authData.access_token;
    },
    clearAuthData(state) {
      state.user = null;
      state.idToken = null;
      state.loginError = '';
      state.isAuthenticated = false;
      state.userDetails = null;
      state.authorizationError = null;
    },
    setLoginError(state, errorMessage) {
      state.loginError = errorMessage;
    },
    setTokenExpiration(state, expiration) {
      state.tokenExpiration = expiration;
    },
    setUserDetails(state, details) {
      state.userDetails = details;
    },
    setAuthorizationError(state, error) {
      state.authorizationError = error;
    },
  },
  actions: {
    async login({ commit, dispatch }, { user, idToken }) {
      try {
        console.log('Login action called with user:', user.email);
        
        // Get token claims to check expiration
        const decodedToken = JSON.parse(atob(idToken.split('.')[1]));
        const expirationTime = decodedToken.exp * 1000;

        // Extract custom claims
        const customClaims = decodedToken?.tenant && decodedToken?.claim ? {
          tenant: decodedToken.tenant,
          claim: decodedToken.claim
        } : null;

        commit('setUser', { ...user, customClaims });
        commit('setIdToken', idToken);
        commit('setTokenExpiration', expirationTime);
        localStorage.setItem('auth_token', idToken);

        // Update to use email directly instead of splitting for username
        await dispatch('fetchUserDetails', { 
          email: user.email 
        });
        
        console.log('User details after login:', store.state.userDetails);
      } catch (error) {
        console.error('Error in login action:', error);
        throw error;
      }
    },
    async logout({ commit }) {
      try {
        // Store the authorization error temporarily before clearing auth data
        const currentError = store.state.authorizationError;
        await auth.signOut();
        commit('clearAuthData');
        // Restore the authorization error after clearing auth data
        if (currentError) {
          commit('setAuthorizationError', currentError);
        }
        localStorage.removeItem('auth_token');
      } catch (error) {
        console.error('Logout error:', error);
        throw error;
      }
    },
    async refreshToken({ commit, state }) {
      if (!state.user) {
        throw new Error('No user authenticated');
      }

      try {
        const newToken = await refreshUserToken();
        if (!newToken) {
          throw new Error('Failed to refresh token');
        }

        const decodedToken = JSON.parse(atob(newToken.split('.')[1]));
        const expirationTime = decodedToken.exp * 1000;
        const customClaims = decodedToken?.tenant && decodedToken?.claim ? {
          tenant: decodedToken.tenant,
          claim: decodedToken.claim
        } : null;

        commit('setUser', { ...state.user, customClaims });
        commit('setIdToken', newToken);
        commit('setTokenExpiration', expirationTime);
        localStorage.setItem('auth_token', newToken);
        
        return newToken;
      } catch (error) {
        console.error('Token refresh failed:', error);
        throw error;
      }
    },
    async fetchUserDetails({ commit, state }, { email }) {
      try {
        const username = email.split('@')[0];
        const tenant = state.user?.customClaims?.tenant;
        
        if (!tenant) {
          throw new Error('No tenant information available');
        }

        const userDocRef = doc(db, `tenants/${tenant}/users`, username);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          commit('setUserDetails', userData);
        } else {
          console.error('No user document found for:', username);
        }
      } catch (error) {
        console.error('Full error:', {
          code: error.code,
          message: error.message,
          details: error
        });
      }
    },
  },
  getters: {
    isAuthenticated: state => state.isAuthenticated,
    currentUser: state => state.user,
    idToken: state => state.idToken,
    username: state => state.user ? state.user.email : '',
    shouldRefreshToken: (state) => {
      if (!state.tokenExpiration) return false;
      const now = Date.now();
      return state.tokenExpiration - now < TOKEN_REFRESH_THRESHOLD;
    },
    firstName: state => state.userDetails?.firstName || '',
    lastName: state => state.userDetails?.lastName || '',
    userRole: state => state.userDetails?.role || '',
  },
});

// Set up Firebase auth state listener
onAuthStateChanged(auth, async (user) => {
  if (user) {
    const idToken = await refreshUserToken();
    store.dispatch('login', { user, idToken });
  } else {
    store.commit('clearAuthData');
  }
});

export default store;
